/**
 * Booking Service for Supabase
 * 
 * This service handles saving all booking data to Supabase tables:
 * - flight_bookings: Main booking information
 * - booking_passengers: Passenger details
 * - booking_payments: Payment information
 * - booking_addons: Additional services
 * - booking_timeline: Booking events
 */

import { supabase } from '../supabaseClient';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

/** Max length for flight_bookings.contact_phone and booking_passengers.phone_number (DB VARCHAR(20)). */
function truncatePhone(value, maxLen = 20) {
  const s = String(value ?? '')
    .trim()
    .replace(/\s/g, '');
  if (!s) return '';
  return s.length > maxLen ? s.slice(0, maxLen) : s;
}

/**
 * Only attach user_id if it exists in public.users — otherwise FK insert fails.
 */
async function resolveFlightBookingsUserId(rawId) {
  if (!isValidUuid(rawId)) {
    if (rawId) {
      console.warn('[Booking] Invalid user_id for flight_bookings; saving as guest:', rawId);
    }
    return null;
  }
  const id = String(rawId).trim();
  const { data, error } = await supabase.from('users').select('id').eq('id', id).maybeSingle();
  if (error) {
    console.warn('[Booking] Could not verify user in public.users:', error.message);
    return null;
  }
  if (!data) {
    console.warn('[Booking] user_id not in public.users (FK); saving as guest:', id);
    return null;
  }
  return id;
}

function mapPassengerTypeForDb(type) {
  const t = String(type || 'adult')
    .toLowerCase()
    .replace(/-/g, '_');
  if (t === 'infant') return 'infant_without_seat';
  const allowed = ['adult', 'child', 'infant_without_seat', 'infant_with_seat'];
  if (allowed.includes(t)) return t;
  return 'adult';
}

/**
 * Save complete booking data to Supabase
 * @param {Object} bookingData - Complete booking information
 * @returns {Promise<Object>} Saved booking data
 */
export const saveBookingToSupabase = async (bookingData) => {
  try {
    const {
      // Booking data
      bookingReference,
      offerId,
      flight,
      flightType = (flight?.isRoundTrip || flight?.return || flight?.returnFlight) ? 'round-trip' : 'one-way',
      
      // Passenger data
      passengers = [],
      passengerData,
      
      // Payment data
      payment,
      paymentMethod = 'stripe',
      
      // Additional options
      additionalOptions = {},
      
      // Duffel booking response
      duffelBooking = null
    } = bookingData;

    // `let` — must be reassignable (localStorage + FK resolution); destructuring would make `const` bindings
    let userId = bookingData.userId ?? null;

    // Get user ID from localStorage if not provided
    if (!userId) {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          userId = user.id || null;
        }
      } catch (error) {
        console.warn('Could not get user ID from localStorage:', error);
      }
    }

    userId = await resolveFlightBookingsUserId(userId);

    // Generate booking reference if not provided
    let finalBookingReference = bookingReference;
    if (!finalBookingReference) {
      const { data: refData, error: refError } = await supabase.rpc('generate_booking_reference');
      if (refError) {
        // Fallback: generate client-side reference
        finalBookingReference = 'SF' + Date.now().toString().slice(-8);
      } else {
        finalBookingReference = refData;
      }
    }

    // Extract flight information
    // Try to get from duffelBooking first, then from flight object
    const firstSlice = duffelBooking?.slices?.[0] || flight?.outbound;
    const returnSlice = duffelBooking?.slices?.[1] || flight?.return || null;
    const firstSegment = firstSlice?.segments?.[0] || firstSlice;
    const lastSegment = firstSlice?.segments?.[firstSlice?.segments?.length - 1] || firstSlice;
    const returnFirstSegment = returnSlice?.segments?.[0] || (flight?.returnFlight ? {
      origin: { iata_code: flight.returnFlight.departureAirport },
      destination: { iata_code: flight.returnFlight.arrivalAirport },
      departing_at: flight.return?.segments?.[0]?.departing_at || null,
      arriving_at: flight.return?.segments?.[flight.return?.segments?.length - 1]?.arriving_at || null,
      marketing_carrier: { name: flight?.airline || 'Airline' },
      marketing_carrier_flight_number: flight?.flightNumber || ''
    } : null);
    const returnLastSegment = returnSlice?.segments?.[returnSlice?.segments?.length - 1] || returnFirstSegment;

    // Helper function to convert ISO 8601 duration to minutes
    const parseDurationToMinutes = (duration) => {
      if (!duration) return null;
      
      // If it's already a number, return it
      if (typeof duration === 'number') return duration;
      
      // If it's a string in ISO 8601 format (PT7H58M)
      if (typeof duration === 'string' && duration.startsWith('PT')) {
        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
        if (match) {
          const hours = parseInt(match[1] || '0', 10);
          const minutes = parseInt(match[2] || '0', 10);
          return (hours * 60) + minutes;
        }
      }
      
      // If it's a number as string, parse it
      const parsed = parseInt(duration, 10);
      if (!isNaN(parsed)) return parsed;
      
      return null;
    };

    // Calculate pricing
    const baseFare = parseFloat(duffelBooking?.baseAmount || flight?.price?.base || flight?.baseFare || '0'.replace(/[₦,]/g, '')) || 0;
    const taxes = parseFloat(duffelBooking?.taxAmount || flight?.price?.taxes || flight?.taxes || '0'.replace(/[₦,]/g, '')) || 0;
    const serviceFee = 0; // Platform service fee
    const totalAmount = parseFloat(duffelBooking?.totalAmount || flight?.price?.total || flight?.total || '0'.replace(/[₦,]/g, '')) || 0;

    // Add additional options to total
    let addonsTotal = 0;
    if (additionalOptions.extraBaggage) addonsTotal += 15000;
    if (additionalOptions.seatSelection) addonsTotal += 5000;
    if (additionalOptions.tripInsurance) {
      const insurancePrice = Math.max(5000, Math.round(totalAmount * 0.05));
      addonsTotal += insurancePrice;
    }

    const finalTotal = totalAmount + addonsTotal;

    // Prepare flight booking data
    const flightBookingData = {
      booking_reference: finalBookingReference,
      user_id: userId,
      offer_id: offerId || flight?.id || flight?.offerId,
      flight_type: flightType,
      
      // Outbound flight details
      outbound_airline: firstSegment?.marketing_carrier?.name || flight?.airline || 'Airline',
      outbound_flight_number: firstSegment?.marketing_carrier_flight_number || flight?.flightNumber || '',
      outbound_departure_airport: firstSegment?.origin?.iata_code || flight?.departureAirport || '',
      outbound_departure_city: firstSegment?.origin?.city_name || flight?.departureCity || '',
      outbound_departure_date: firstSegment?.departing_at ? new Date(firstSegment.departing_at).toISOString().split('T')[0] : null,
      outbound_departure_time: firstSegment?.departing_at ? new Date(firstSegment.departing_at).toTimeString().slice(0, 8) : null,
      outbound_arrival_airport: lastSegment?.destination?.iata_code || flight?.arrivalAirport || '',
      outbound_arrival_city: lastSegment?.destination?.city_name || flight?.arrivalCity || '',
      outbound_arrival_date: lastSegment?.arriving_at ? new Date(lastSegment.arriving_at).toISOString().split('T')[0] : null,
      outbound_arrival_time: lastSegment?.arriving_at ? new Date(lastSegment.arriving_at).toTimeString().slice(0, 8) : null,
      outbound_duration: parseDurationToMinutes(firstSlice?.duration),
      outbound_stops: (firstSlice?.segments?.length || 1) - 1,
      
      // Return flight details (if round-trip)
      // Try to get from returnSlice first, then from flight.returnFlight
      return_airline: returnFirstSegment?.marketing_carrier?.name || flight?.airline || null,
      return_flight_number: returnFirstSegment?.marketing_carrier_flight_number || flight?.flightNumber || null,
      return_departure_airport: returnFirstSegment?.origin?.iata_code || flight?.returnFlight?.departureAirport || null,
      return_departure_city: returnFirstSegment?.origin?.city_name || (flight?.returnFlight?.departureAirport === 'LOS' ? 'Lagos' : 
                             flight?.returnFlight?.departureAirport === 'ABV' ? 'Abuja' : 
                             flight?.returnFlight?.departureAirport === 'LHR' ? 'London' : 
                             flight?.returnFlight?.departureAirport === 'DXB' ? 'Dubai' : 
                             flight?.returnFlight?.departureAirport === 'ACC' ? 'Accra' : 
                             flight?.returnFlight?.departureAirport === 'CDG' ? 'Paris' : null) || null,
      return_departure_date: returnFirstSegment?.departing_at ? new Date(returnFirstSegment.departing_at).toISOString().split('T')[0] : 
                             (flight?.return?.segments?.[0]?.departing_at ? new Date(flight.return.segments[0].departing_at).toISOString().split('T')[0] : null),
      return_departure_time: returnFirstSegment?.departing_at ? new Date(returnFirstSegment.departing_at).toTimeString().slice(0, 8) : 
                             (flight?.return?.segments?.[0]?.departing_at ? new Date(flight.return.segments[0].departing_at).toTimeString().slice(0, 8) : null),
      return_arrival_airport: returnLastSegment?.destination?.iata_code || flight?.returnFlight?.arrivalAirport || null,
      return_arrival_city: returnLastSegment?.destination?.city_name || (flight?.returnFlight?.arrivalAirport === 'LOS' ? 'Lagos' : 
                            flight?.returnFlight?.arrivalAirport === 'ABV' ? 'Abuja' : 
                            flight?.returnFlight?.arrivalAirport === 'LHR' ? 'London' : 
                            flight?.returnFlight?.arrivalAirport === 'DXB' ? 'Dubai' : 
                            flight?.returnFlight?.arrivalAirport === 'ACC' ? 'Accra' : 
                            flight?.returnFlight?.arrivalAirport === 'CDG' ? 'Paris' : null) || null,
      return_arrival_date: returnLastSegment?.arriving_at ? new Date(returnLastSegment.arriving_at).toISOString().split('T')[0] : 
                          (flight?.return?.segments && flight.return.segments.length > 0 ? 
                           new Date(flight.return.segments[flight.return.segments.length - 1].arriving_at).toISOString().split('T')[0] : null),
      return_arrival_time: returnLastSegment?.arriving_at ? new Date(returnLastSegment.arriving_at).toTimeString().slice(0, 8) : 
                          (flight?.return?.segments && flight.return.segments.length > 0 ? 
                           new Date(flight.return.segments[flight.return.segments.length - 1].arriving_at).toTimeString().slice(0, 8) : null),
      return_duration: parseDurationToMinutes(returnSlice?.duration || flight?.returnFlight?.duration || flight?.return?.duration),
      return_stops: returnSlice ? (returnSlice.segments?.length || 1) - 1 : (flight?.returnFlight?.stops || 0),
      
      // Pricing
      base_fare: baseFare,
      taxes: taxes,
      service_fee: serviceFee,
      total_amount: finalTotal,
      currency: 'NGN',
      
      // Booking details - convert cabin class to lowercase and map to valid values
      cabin_class: (() => {
        const cabinClass = (firstSlice?.cabin_class_name || firstSlice?.cabin_class || flight?.travelClass || 'economy').toLowerCase().trim();
        // Map common variations to valid database values
        if (cabinClass === 'premium economy' || cabinClass === 'premium_economy') return 'premium_economy';
        if (cabinClass === 'economy' || cabinClass === 'coach') return 'economy';
        if (cabinClass === 'business' || cabinClass === 'business class') return 'business';
        if (cabinClass === 'first' || cabinClass === 'first class') return 'first';
        // Default to economy if unknown
        return 'economy';
      })(),
      number_of_passengers: passengers.length || 1,
      booking_status: 'confirmed',
      payment_status: payment?.status === 'succeeded' ? 'paid' : 'pending',
      
      // Contact information (NOT NULL in DB; phone max 20 chars)
      contact_email: (() => {
        const e = String(passengers[0]?.email || passengerData?.email || '')
          .trim()
          .slice(0, 255);
        return e || `guest-${String(finalBookingReference).replace(/[^a-zA-Z0-9]/g, '')}@booking.local`;
      })(),
      contact_phone: (() => {
        const p = truncatePhone(
          passengers[0]?.phone_number || passengerData?.phoneNumber || passengerData?.phone || ''
        );
        return p || '0000000000';
      })(),
      
      // Additional information
      special_requests: null,
      notes: null,
      
      // Timestamps
      booking_date: new Date().toISOString(),
      payment_date: payment?.status === 'succeeded' ? new Date().toISOString() : null,
      confirmation_date: new Date().toISOString()
    };

    // Insert flight booking
    console.log('💾 Attempting to save flight booking to Supabase...');
    console.log('📋 Booking data (duration converted to minutes):', JSON.stringify({
      ...flightBookingData,
      outbound_duration: flightBookingData.outbound_duration,
      return_duration: flightBookingData.return_duration
    }, null, 2));
    
    const { data: savedBooking, error: bookingError } = await supabase
      .from('flight_bookings')
      .insert([flightBookingData])
      .select()
      .single();

    if (bookingError) {
      console.error('❌ Error saving flight booking:', bookingError);
      console.error('❌ Error details:', {
        message: bookingError.message,
        details: bookingError.details,
        hint: bookingError.hint,
        code: bookingError.code
      });
      throw new Error(`Failed to save booking: ${bookingError.message} (Code: ${bookingError.code})`);
    }

    console.log('✅ Flight booking saved to Supabase:', savedBooking.id);
    console.log('✅ Saved booking:', savedBooking);

    // Save passengers
    const savedPassengers = [];
    const passengerList = passengers.length > 0 ? passengers : [passengerData];

    for (const passenger of passengerList) {
      // Split name if needed
      let firstName = passenger.given_name || passenger.firstName || '';
      let lastName = passenger.family_name || passenger.lastName || '';
      let fullName = passenger.full_name || passenger.fullName || '';

      if (!firstName && !lastName && fullName) {
        const nameParts = fullName.trim().split(/\s+/);
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      if (!fullName && (firstName || lastName)) {
        fullName = `${firstName} ${lastName}`.trim();
      }

      const passengerDataToSave = {
        booking_id: savedBooking.id,
        passenger_type: mapPassengerTypeForDb(
          passenger.type || passenger.passengerType || passenger.passenger_type
        ),
        title: passenger.title || passengerData?.title || null,
        first_name: firstName || 'Unknown',
        last_name: lastName || 'Unknown',
        full_name: fullName || 'Unknown',
        date_of_birth: passenger.born_on || passenger.dateOfBirth || null,
        gender: passenger.gender === 'm' ? 'male' : passenger.gender === 'f' ? 'female' : passenger.gender || passengerData?.gender?.toLowerCase() || null,
        nationality: passenger.nationality || passengerData?.nationality || null,
        id_type: passengerData?.nin ? 'nin' : passengerData?.passportNumber ? 'passport' : null,
        id_number: passengerData?.nin || null,
        passport_number: passengerData?.passportNumber || passenger.passport_number || null,
        passport_expiry_date: passengerData?.passportExpiryDate || null,
        passport_issuing_country: passengerData?.passportIssuingCountry || null,
        email: passenger.email || passengerData?.email || '',
        phone_number: truncatePhone(
          passenger.phone_number || passengerData?.phoneNumber || passengerData?.phone || ''
        ) || '0000000000',
        emergency_contact_name: passengerData?.nextOfKin || null,
        emergency_contact_phone: passengerData?.emergencyContact || null,
        emergency_contact_relationship: null,
        seat_number: passenger.seat || null,
        seat_preference: null,
        special_assistance: false,
        special_meals: null,
        wheelchair_assistance: false,
        duffel_passenger_id: passenger.id || null
      };

      const { data: savedPassenger, error: passengerError } = await supabase
        .from('booking_passengers')
        .insert([passengerDataToSave])
        .select()
        .single();

      if (passengerError) {
        console.error('❌ Error saving passenger:', passengerError);
        console.error('❌ Passenger error details:', {
          message: passengerError.message,
          details: passengerError.details,
          hint: passengerError.hint,
          code: passengerError.code
        });
        // Continue with other passengers even if one fails
      } else {
        savedPassengers.push(savedPassenger);
        console.log('✅ Passenger saved:', savedPassenger.id);
      }
    }

    // Save payment information
    if (payment) {
      const paymentData = {
        booking_id: savedBooking.id,
        user_id: userId,
        payment_method: paymentMethod,
        payment_reference: payment.paymentIntentId || payment.transactionId || null,
        transaction_id: payment.paymentIntentId || payment.transactionId || null,
        amount: finalTotal,
        currency: 'NGN',
        exchange_rate: 1.0,
        payment_status: payment.status === 'succeeded' ? 'completed' : 'pending',
        gateway_response: payment,
        gateway_transaction_id: payment.paymentIntentId || payment.transactionId || null,
        payment_date: new Date().toISOString()
      };

      const { data: savedPayment, error: paymentError } = await supabase
        .from('booking_payments')
        .insert([paymentData])
        .select()
        .single();

      if (paymentError) {
        console.error('❌ Error saving payment:', paymentError);
        console.error('❌ Payment error details:', {
          message: paymentError.message,
          details: paymentError.details,
          hint: paymentError.hint,
          code: paymentError.code
        });
        // Don't throw, payment might be saved later
      } else {
        console.log('✅ Payment saved:', savedPayment.id);
      }
    }

    // Save addons
    if (additionalOptions.extraBaggage) {
      const addonData = {
        booking_id: savedBooking.id,
        addon_type: 'extra_baggage',
        addon_name: 'Extra Baggage (10kg)',
        addon_description: 'Additional 10kg checked baggage',
        quantity: 1,
        unit_price: 15000,
        total_price: 15000,
        currency: 'NGN',
        status: 'active'
      };

      const { error: addonError } = await supabase.from('booking_addons').insert([addonData]);
      if (addonError) {
        console.error('❌ Error saving extra baggage addon:', addonError);
      } else {
        console.log('✅ Extra baggage addon saved');
      }
    }

    if (additionalOptions.seatSelection) {
      const addonData = {
        booking_id: savedBooking.id,
        addon_type: 'seat_selection',
        addon_name: 'Seat Selection',
        addon_description: 'Preferred seat selection',
        quantity: 1,
        unit_price: 5000,
        total_price: 5000,
        currency: 'NGN',
        status: 'active'
      };

      const { error: addonError } = await supabase.from('booking_addons').insert([addonData]);
      if (addonError) {
        console.error('❌ Error saving seat selection addon:', addonError);
      } else {
        console.log('✅ Seat selection addon saved');
      }
    }

    if (additionalOptions.tripInsurance) {
      const insurancePrice = Math.max(5000, Math.round(totalAmount * 0.05));
      const addonData = {
        booking_id: savedBooking.id,
        addon_type: 'travel_insurance',
        addon_name: 'Trip Insurance',
        addon_description: 'Travel insurance coverage',
        quantity: 1,
        unit_price: insurancePrice,
        total_price: insurancePrice,
        currency: 'NGN',
        status: 'active'
      };

      const { error: addonError } = await supabase.from('booking_addons').insert([addonData]);
      if (addonError) {
        console.error('❌ Error saving trip insurance addon:', addonError);
      } else {
        console.log('✅ Trip insurance addon saved');
      }
    }

    // Create timeline entry
    const { error: timelineError } = await supabase.rpc('add_booking_timeline_entry', {
      p_booking_id: savedBooking.id,
      p_event_type: 'created',
      p_event_description: 'Booking created and confirmed',
      p_performed_by: userId,
      p_performed_by_type: userId ? 'user' : 'system'
    });
    
    if (timelineError) {
      console.error('❌ Error creating timeline entry:', timelineError);
      // Try direct insert as fallback
      const { error: directTimelineError } = await supabase
        .from('booking_timeline')
        .insert([{
          booking_id: savedBooking.id,
          event_type: 'created',
          event_description: 'Booking created and confirmed',
          performed_by: userId,
          performed_by_type: userId ? 'user' : 'system'
        }]);
      
      if (directTimelineError) {
        console.error('❌ Error with direct timeline insert:', directTimelineError);
      } else {
        console.log('✅ Timeline entry created (direct insert)');
      }
    } else {
      console.log('✅ Timeline entry created');
    }

    console.log('✅ Complete booking data saved to Supabase');

    return {
      success: true,
      booking: savedBooking,
      passengers: savedPassengers,
      bookingReference: finalBookingReference
    };

  } catch (error) {
    console.error('Error saving booking to Supabase:', error);
    throw error;
  }
};

/**
 * Get booking by reference
 */
export const getBookingByReference = async (bookingReference) => {
  try {
    const { data, error } = await supabase
      .from('flight_bookings')
      .select(`
        *,
        booking_passengers (*),
        booking_payments (*),
        booking_addons (*),
        booking_timeline (*)
      `)
      .eq('booking_reference', bookingReference)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error getting booking:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all bookings for a user
 */
export const getUserBookings = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('flight_bookings')
      .select(`
        *,
        booking_passengers (*),
        booking_payments (*)
      `)
      .eq('user_id', userId)
      .order('booking_date', { ascending: false });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error getting user bookings:', error);
    return { success: false, error: error.message };
  }
};
